import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { RequireAuth } from '@/shared/auth/RequireAuth';
import { ToastProvider } from '@/app/providers/ToastProvider';
import { AuthProvider } from '@/app/providers/AuthProvider';
import { TenantScopeProvider } from '@/app/providers/TenantScopeProvider';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { CommandPalette } from './CommandPalette';
import { PlanModal } from './PlanModal';
import { usePlanRefresh } from './usePlanRefresh';
import { BusinessTypeGate } from './BusinessTypeGate';
import { useZoomResizeDispatch } from '@/shared/hooks/useZoomResizeDispatch';

/** Sidebar-ийн хумигдсан төлвийг хадгалах localStorage түлхүүр. */
const COLLAPSED_KEY = 'ts-sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function ShellInner() {
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  // Утас / таблет: sidebar off-canvas — hamburger дарахад нээгдэнэ.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const loc = useLocation();
  usePlanRefresh();
  useZoomResizeDispatch();

  const toggleCollapsed = useCallback(() => {
    setCollapsed((x) => {
      const next = !x;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* хадгалахгүй байж болно */
      }
      return next;
    });
  }, []);

  // Хуудас солигдоход mobile цэсийг хаана.
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  // Mobile цэс нээлттэй үед Esc — хаах; body scroll-ыг түгжинэ.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <>
      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onUpgrade={() => setPlanOpen(true)}
      />
      <div
        className={clsx('ts-sidebar-backdrop', mobileOpen && 'open')}
        aria-hidden="true"
        onClick={() => setMobileOpen(false)}
      />
      <Header
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        onToggleMobile={() => setMobileOpen((x) => !x)}
        onOpenPlanModal={() => setPlanOpen(true)}
      />
      <main className={clsx('ts-main', collapsed && 'collapsed')}>
        <BusinessTypeGate />
      </main>
      <CommandPalette onUpgrade={() => setPlanOpen(true)} />
      <PlanModal open={planOpen} onClose={() => setPlanOpen(false)} />
    </>
  );
}

export function ShellLayout() {
  return (
    <AuthProvider>
      <ToastProvider>
        <RequireAuth>
          <TenantScopeProvider>
            <ShellInner />
          </TenantScopeProvider>
        </RequireAuth>
      </ToastProvider>
    </AuthProvider>
  );
}
